import { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "src/auth/auth.ts";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { useParams, useSearchParams } from "react-router-dom";
import { useGlobalSettings } from "src/hooks/use-global-settings.ts";
import { useDataProvider } from "src/system/useDataProvider.ts";
import type { ResourceFormContextValue } from "src/components/Form/ResourceFormContext.tsx";
import type { DefaultValues, FieldValues } from "react-hook-form";
import type { ValidParams } from "src/lib/react-router/types.ts";
import type { StorageKey } from "@exonn/storage";
import { useDeleteOne } from "src/system/useDeleteOne.ts";
import { useCloneToLocale } from "src/system/useCloneToLocale.ts";
import { useContentLocale } from "src/auth/hooks/useContentLocale.tsx";
import { useResourceFormLangInternal } from "src/components/Form/hooks/useResourceForm/useResourceFormLangInternal.ts";
import { useResourceFormInitialFormValues } from "src/components/Form/hooks/useResourceForm/useResourceFormInitialFormValues.ts";
import { useResourceFormChangeLanguage } from "src/components/Form/hooks/useResourceForm/useResourceFormChangeLanguage.ts";
import { useResourceFormRefresh } from "src/components/Form/hooks/useResourceForm/useResourceFormRefresh.ts";
import { useResourceFormConfig } from "src/components/Form/hooks/useResourceForm/useResourceFormConfig.ts";
import { useResourceFormTranslationsSuspenseQuery } from "src/components/Form/hooks/useResourceForm/useResourceFormTranslationsSuspenseQuery.ts";
import axios from "axios";
import { logger } from "src/logger";
import { useSnackbar } from "src/App/App.hooks.ts";
import { replacePlaceholders } from "src/components/Form/utils/replacePlaceholders.ts";
import { mapObject } from "@exonn/utils";
import { isPlainObject } from "lodash";
import { useResourceStorageKeys } from "src/framework/useResourceStorageKeys.tsx";
import { useCreateForm } from "src/system/useCreateForm.tsx";
import { useOne } from "src/system/useOne.tsx";
import { useForm as useReactHookForm } from "src/lib/react-hook-form/useForm.tsx";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";
import { useUpdateOne } from "src/system/useUpdateOne.ts";
import { useCreateOne } from "src/system/useCreateOne.ts";
import type { TFunction } from "i18next";
import type { FormAction } from "src/components/Form/Form.types.ts";
import { useErpTranslation } from "src/system/useErpTranslation.ts";

export type ValidSaveFormResponseData = {
  id?: string;
  data: {
    id: string;
  };
};
export type ValidFormData = {
  data?: any;
  [key: string]: any;
};

export type ValidRequestData = {
  [key: string]: any;
};

export type FormErrorResponse = {
  data: {
    errors: {
      [name: string]: string[];
    };
  };
};

export type UseResourceFormProps = {
  resource: string;
  onDirtyChange?: (isDirty: boolean) => void;
  meta?: any;
  tab?: string;
  onDeleteSuccess?: () => void;
  onSaveSuccess?: (response: ValidSaveFormResponseData) => void;
  storageKey?: StorageKey;
  langStorageKey?: StorageKey;
  id?: string;
  includes?: string[];
  action: FormAction;
  notification?: {
    formWasSaved?: (t: TFunction) => string;
  };
};

export const useResourceForm = <
  FormValues extends FieldValues = FieldValues,
  Params extends ValidParams = ValidParams,
  FormData extends ValidFormData = ValidFormData,
  RequestData extends ValidRequestData = ValidRequestData,
>({
  resource,
  onDirtyChange,
  onDeleteSuccess,
  id,
  action,
  tab,
  meta,
  onSaveSuccess,
  notification,
}: UseResourceFormProps) => {
  const { data: user } = useUser();
  const clientConfig = useClientConfig();
  const [refreshCount, setRefreshCount] = useState(0);

  const { mutate: deleteResource } = useDeleteOne({
    resource,
  });

  const defaultContentLocale = useContentLocale()!;

  const { lang, setLang } = useResourceFormLangInternal({
    defaultLocale: defaultContentLocale,
    resource,
    action,
    tab,
    meta,
    id,
  });

  const { t } = useErpTranslation({
    type: "form",
    resource,
    tab,
    formType: action,
  });
  const params = useParams() as Params;
  const [searchParams] = useSearchParams();
  const dataProvider = useDataProvider();

  const { formConfig, refetchFormConfig } = useResourceFormConfig<
    FormValues,
    Params,
    FormData,
    RequestData
  >({
    resource,
    tab,
    action,
  });

  const {
    fields,
    disabled_fields,
    hidden_fields,
    transformValuesToRequestData: transformValuesToRequestDataProp,
    transformResourceToValues,
    transformDataToValues,
    getAttributePlaceholderReplacement,
    mapValidationErrors,
    includes,
  } = formConfig;

  const { refetch: refetchTranslations } =
    useResourceFormTranslationsSuspenseQuery({
      resource,
      action,
      tab,
    });

  const { data: globalSettings } = useGlobalSettings();

  const resolveFields = useCallback(
    (data: FormData | undefined) =>
      typeof fields === "function"
        ? fields({
            data,
            t,
            searchParams,
            clientConfig,
            type: action,
            params,
            user,
            dataProvider,
            globalSettings,
          })
        : fields,
    [
      fields,
      params,
      t,
      searchParams,
      clientConfig,
      user,
      dataProvider,
      globalSettings,
      action,
    ],
  );

  const getInitialValues = useResourceFormInitialFormValues({
    transformDataToValues,
    transformResourceToValues,
    resource,
    action,
    globalSettings,
    resolveFields,
  });

  const getFieldLabelTranslation = useCallback(
    (name: string) => {
      const keys = [
        `form.fields.${name}.label`,
        `form.fields.${name}`,
        `form.field.${name}.label`,
        `form.field.${name}`,
        `field.${name}.label`,
        `field.${name}`,
      ];
      for (const k of keys) {
        const value = t(k, { defaultValue: "" });
        if (value && value !== k) return value;
      }
      return name;
    },
    [t],
  );

  const transformValuesToRequestData = useCallback(
    (
      values: FormValues,
      data: FormData,
      type: FormAction,
    ): RequestData | Promise<RequestData> => {
      const processedValues = values;
      // todo: implement per field transformer
      if (transformValuesToRequestDataProp) {
        return transformValuesToRequestDataProp(
          processedValues,
          data,
          type,
          globalSettings,
        );
      }

      return processedValues as unknown as RequestData;
    },
    [transformValuesToRequestDataProp, globalSettings],
  );

  const locale = lang || defaultContentLocale;

  if (action === "edit" && !id) {
    throw new Error(
      "useResourceForm: id should be specified if 'action' prop is 'edit'",
    );
  }

  const storageKeys = useResourceStorageKeys({
    resource,
  });

  const snackbar = useSnackbar();

  const storageKey = useMemo(() => {
    return storageKeys.form({
      id: id!,
      type: action,
      tab,
      meta: meta,
    });
  }, [id, storageKeys, action, meta, tab]);

  const createQueryResult = useCreateForm({
    resource,
    locale,
    meta,
    queryOptions: {
      structuralSharing: false,
      throwOnError: false,
      refetchOnMount: true,
      staleTime: Infinity,
      enabled: action === "create",
    },
  });

  const editQueryResult = useOne({
    lang: locale,
    id: id!,
    includes: includes,
    resource,
    meta,
    queryOptions: {
      structuralSharing: false,
      throwOnError: false,
      refetchOnMount: true,
      staleTime: Infinity,
      enabled: action === "edit",
    },
  });

  const queryResult = action === "edit" ? editQueryResult : createQueryResult;

  const { data, isFetching, refetch } = queryResult;

  const initialValues = useMemo(() => {
    return typeof getInitialValues === "function"
      ? getInitialValues({
          data,
        })
      : getInitialValues;
  }, [data, getInitialValues]);

  const defaultValues = initialValues as DefaultValues<FormValues>;

  const {
    form,
    getValuesFromStorage,
    clearStorage,
    disablePersistence,
    enablePersistence,
  } = useReactHookForm<FormValues>({
    defaultValues: defaultValues as DefaultValues<FormValues>,
    storageKey,
    storage: memoryStorageDriver,
  });

  const handleError = useCallback(
    (e: any) => {
      if (!axios.isAxiosError(e)) {
        return;
      }

      if (!e.response) {
        return;
      }
      // todo: wrap in try catch
      const response = e.response as FormErrorResponse;
      const errors = response.data.errors;

      const resolvedFields = resolveFields(queryResult.data);

      const message = e.response?.data?.message;
      const getFieldByErrorName = (errorName: string) => {
        const name = errorName.split(".")[0];
        return resolvedFields[name];
      };

      const getFieldLabel = (errorName: string) => {
        const name = errorName;

        const customAttributePlaceholder = getAttributePlaceholderReplacement?.(
          t,
          errorName,
        );

        if (customAttributePlaceholder) {
          return customAttributePlaceholder;
        }

        const field = getFieldByErrorName(errorName);
        if (field) {
          if ("label" in field && typeof field.label === "string") {
            return field.label;
          }
        }
        return getFieldLabelTranslation(name);
      };

      if (message) {
        const firstErrorFieldName = isPlainObject(errors)
          ? Object.keys(errors)?.[0]
          : "";
        snackbar.error({
          flash: true,
          message: replacePlaceholders(message, {
            attribute: getFieldLabel(firstErrorFieldName),
          }),
        });
      }

      const defaultValidationErrorsMapper = (
        errors: Record<string, string[]>,
        values: any,
      ) => errors;

      // run global mapper
      const errorsAfterGlobalMapper = (
        mapValidationErrors || defaultValidationErrorsMapper
      )(errors, form.getValues(), globalSettings);

      // run per field mappers
      const mappedErrorsAfterPerFieldMappers = Object.keys(
        resolvedFields,
      ).reduce(
        (acc: Record<string, string[]>, fieldName: string) => {
          const field = resolvedFields[fieldName];
          const mapper = field.mapError;
          if (mapper) {
            const value = mapper(response.data.errors);
            return {
              ...acc,
              [fieldName]: value,
            };
          }
          return {
            ...acc,
          };
        },
        errorsAfterGlobalMapper as Record<string, string[]>,
      );

      mapObject(mappedErrorsAfterPerFieldMappers, function (errors, errorName) {
        const field = resolvedFields[errorName];
        const error = errors?.[0];
        if (!error) {
          return;
        }
        if (!field) {
          form.setError(`root.${errorName}`, {
            type: "manual",
            message: replacePlaceholders(error, {
              attribute: getFieldLabel(errorName),
            }),
          });
          return;
        }

        form.setError(`root.${errorName}`, {
          type: "manual",
          message: replacePlaceholders(error, {
            attribute: getFieldLabel(errorName),
          }),
        });
      });
    },
    [
      form,
      getAttributePlaceholderReplacement,
      getFieldLabelTranslation,
      mapValidationErrors,
      resolveFields,
      snackbar,
      queryResult.data,
      globalSettings,
      t,
    ],
  );

  const onError = useCallback(
    (e: Error) => {
      try {
        handleError(e);
      } catch (e: any) {
        logger.error(e);
      }
    },
    [handleError],
  );

  useEffect(() => {
    const persistedValues = getValuesFromStorage();
    if (persistedValues || isFetching) {
      return;
    }

    disablePersistence();

    form.reset(initialValues);
    enablePersistence();
  }, [
    getValuesFromStorage,
    form,
    initialValues,
    isFetching,
    action,
    disablePersistence,
    enablePersistence,
  ]);

  const { mutate: update } = useUpdateOne<RequestData>({
    resource: resource,
    meta,
    tab,
    additionalHeaders: {
      "dont-replace-attribute-placeholder-in-validation-message": true,
    },
    onSuccess: (response) => {
      clearStorage();

      // todo: try catch
      onSaveSuccess?.(response);
    },
  });

  const { mutate: create } = useCreateOne<RequestData>({
    resource: resource,
    meta,
    additionalHeaders: {
      "dont-replace-attribute-placeholder-in-validation-message": true,
    },
    onSuccess: (response) => {
      clearStorage();

      // todo: try catch
      onSaveSuccess?.(response);
    },
  });

  const handleCreate = useCallback(
    (params?: {
      onError?: (error: any) => void;
      onSuccess?: () => void;
      additionalRequestData?: Record<string, any>;
    }) =>
      async (values: FormValues) => {
        try {
          const requestData = {
            ...(await transformValuesToRequestData(values, data, action)),
            ...params?.additionalRequestData,
          };
          create(
            {
              successNotification: {
                message: notification?.formWasSaved?.(t),
              },
              data: requestData,
              locale: locale,
              disableErrorNotification: true,
            },
            {
              onSuccess: () => {
                params?.onSuccess?.();
              },
              onError: (e: any) => {
                params?.onError?.(e);
                onError?.(e);
              },
            },
          );
        } catch (error: any) {
          logger.error(error);
          snackbar.error({
            flash: true,
            message: "Something went wrong",
          });
          throw error;
        }
      },
    [
      snackbar,
      data,
      locale,
      notification,
      onError,
      t,
      action,
      transformValuesToRequestData,
      create,
    ],
  );

  const handleUpdate = useCallback(
    (params?: {
      onError?: (error: any) => void;
      onSuccess?: () => void;
      additionalRequestData?: Record<string, any>;
    }) =>
      async (values: FormValues) => {
        try {
          const requestData = {
            ...(await transformValuesToRequestData(values, data, action)),
            ...params?.additionalRequestData,
          };
          update(
            {
              id: id!,
              locale: locale,
              successNotification: {
                message: notification?.formWasSaved?.(t),
              },
              disableErrorNotification: true,
              data: requestData,
            },
            {
              onSuccess: () => {
                params?.onSuccess?.();
              },
              onError: (e: any) => {
                params?.onError?.(e);
                onError?.(e);
              },
            },
          );
        } catch (error: any) {
          logger.error(error);
          snackbar.error({
            flash: true,
            message: "Something went wrong",
          });
          throw error;
        }
      },
    [
      snackbar,
      data,
      id,
      locale,
      notification,
      onError,
      action,
      t,
      transformValuesToRequestData,
      update,
    ],
  );

  const handleSubmit = useMemo(
    () => (action === "edit" ? handleUpdate({}) : handleCreate({})),
    [action, handleUpdate, handleCreate],
  );

  const handleSubmitWithParams = useCallback(
    (params?: {
      onError?: (error: any) => void;
      onSuccess?: () => void;
      additionalRequestData?: Record<string, any>;
    }) => {
      return action === "edit" ? handleUpdate(params) : handleCreate(params);
    },
    [action, handleUpdate, handleCreate],
  );

  const onSubmit = handleSubmit;
  const onSubmitWithParams = handleSubmitWithParams;
  const availableLocales = useMemo(
    () => (data ? data?.available_languages ?? [] : []),
    [data],
  );

  const { mutate: copyLocale } = useCloneToLocale({
    resource,
    lang,
    id: id!,
  });

  const resolvedFields = useMemo(
    () =>
      typeof fields === "function"
        ? fields({
            data: data,
            t: t,
            searchParams,
            params,
            type: action,
            user,
            dataProvider,
            globalSettings,
            clientConfig,
          })
        : fields,
    [
      params,
      data,
      t,
      action,
      searchParams,
      user,
      dataProvider,
      globalSettings,
      fields,
      clientConfig,
    ],
  );

  const changeLanguage = useResourceFormChangeLanguage({
    resource,
    id,
    setLang,
    clearStorage,
    copyLocale,
    t,
  });

  const refresh = useResourceFormRefresh<FormValues>({
    resource,
    action,
    id,
    refetch,
    initialValues,
    form,
    clearStorage,
    refetchTranslations,
    refetchFormConfig,
    setRefreshCount,
  });

  const handleDelete = useCallback(() => {
    deleteResource(
      {
        id: id!,
      },
      {
        onSuccess: () => {
          onDeleteSuccess?.();
        },
      },
    );
  }, [id, deleteResource, onDeleteSuccess]);

  const save = useCallback(
    (params?: {
      onSuccess?: () => void;
      onError?: (error: any) => void;
      additionalRequestData?: Record<string, any>;
    }) => {
      form.handleSubmit(onSubmitWithParams(params))();
    },
    [form, onSubmitWithParams],
  );

  const hasUnsavedChanges = useCallback(() => {
    return !!getValuesFromStorage();
  }, [getValuesFromStorage]);

  const formApi = useMemo(
    () => ({
      changeLanguage,
      refresh,
      delete: handleDelete,
      save,
      hasUnsavedChanges,
    }),
    [changeLanguage, refresh, handleDelete, save, hasUnsavedChanges],
  );

  const layoutProp = formConfig.layout;

  const layout = useMemo(() => {
    return typeof layoutProp === "function"
      ? layoutProp({
          t,
          data: data,
          globalSettings,
          params,
          type: action,
          dataProvider,
          clientConfig,
        })
      : layoutProp;
  }, [
    data,
    layoutProp,
    params,
    t,
    action,
    dataProvider,
    clientConfig,
    globalSettings,
  ]);

  const formContext = useMemo<ResourceFormContextValue<any>>(
    () => ({
      action,
      id,
      resource,
      tab,
      locale: lang,
      fields: resolvedFields,
      refreshCount,
      onSubmit,
      onSubmitWithParams,
      includes,
      availableLocales,
      meta,
      lang,
      hidden_fields,
      disabled_fields,
      layoutConfig: layout,
    }),
    [
      action,
      id,
      resource,
      lang,
      tab,
      meta,
      includes,
      refreshCount,
      hidden_fields,
      disabled_fields,
      resolvedFields,
      onSubmit,
      layout,
      availableLocales,
      onSubmitWithParams,
    ],
  );

  return {
    formContext,
    formApi,
    form,
    queryResult,
    layout,
  };
};
